import api from '../api/apiClient';

/** Read-only account-deletion dashboard API (GET /api/v1/admin/account-deletions). */

export type DeletionLedgerStatus =
  | 'scheduled'
  | 'blocked'
  | 'processing'
  | 'needs_review'
  | 'failed'
  | 'completed'
  | 'cancelled';

export type DeletionDisplayStatus =
  | 'pending'
  | 'cancellation_window'
  | 'awaiting_approval'
  | 'approved'
  | 'on_hold'
  | 'in_progress'
  | 'needs_review'
  | 'failed'
  | 'executed_verification_pending'
  | 'completed'
  | 'cancelled';

export interface DeletionApproval {
  state: 'none' | 'pending' | 'expired' | 'used' | 'revoked';
  createdAt?: string | null;
  expiresAt?: string | null;
  usedAt?: string | null;
  revokedAt?: string | null;
}

export interface DeletionExecution {
  state: 'not_started' | 'in_progress' | 'stopped' | 'executed';
  stepsCompleted: number;
  stepsTotal: number;
  startedAt: string | null;
  completedAt: string | null;
  attempts?: number;
  lastAttemptAt?: string | null;
  nextAttemptAt?: string | null;
  reviewReason?: string | null;
}

export interface DeletionProcessStatus {
  /** The production deletion policy is approved in code. */
  productionPolicyActive: boolean;
  /** true / false, or null when it could not be read (treated as a refusal). */
  emergencyStop: boolean | null;
  /** Stripe and other provider checks run only in the operator's dry run. */
  providerChecks: 'not_performed';
  operatorEligibility: 'determined_by_operator_dry_run';
  cancellationDays: number;
  completionTargetDays: number | null;
}

export interface DeletionRequestRow {
  id: string;
  userId: string;
  userType: 'client' | 'stylist' | 'admin' | null;
  requestedAt: string | null;
  cancellationDeadline: string | null;
  cancellationWindowOpen: boolean;
  status: DeletionLedgerStatus;
  displayStatus: DeletionDisplayStatus;
  cancelledAt: string | null;
  approval: DeletionApproval;
  execution: DeletionExecution;
  completion: { fullyComplete: boolean; outstanding: string[] };
  holds: string[];
  updatedAt: string | null;
}

export interface DeletionRequestList {
  success: boolean;
  items: DeletionRequestRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  summary: {
    byStatus: Partial<Record<DeletionLedgerStatus, number>>;
    openRequestsWithoutLedger: number;
    openRequestsWithoutLedgerCapped: boolean;
  };
  process: DeletionProcessStatus;
}

export interface DeletionRequestDetail extends Omit<DeletionRequestRow, 'holds' | 'completion'> {
  timeline: {
    requestedAt: string | null;
    cancellationDeadline: string | null;
    cancelledAt: string | null;
    approvedAt: string | null;
    executionStartedAt: string | null;
    executedAt: string | null;
    fullyVerified: boolean;
  };
  milestones: {
    requestReceived: boolean;
    requestApproved: boolean;
    deletionExecuted: boolean;
    deletionFullyVerified: boolean;
  };
  holds: {
    recorded: string[];
    live: string[];
    liveChecked: boolean;
    liveCheck: 'checked' | 'unavailable' | 'not_applicable';
    review: {
      reviewsRecorded: number;
      nextDueAt: string;
      overdue: boolean;
      tier: number;
      reviewer: string;
    } | null;
    reviewsRecorded: { at: string; outcome: string; tier: number }[];
  };
  completion: {
    fullyComplete: boolean;
    outstanding: string[];
    mediaPending: number;
    mediaAbandoned: number;
    mediaUnmanaged: number;
    mediaCleanupComplete: boolean;
    stripeConnectClosure: string;
    residualNotificationReview: string;
    legacyOriginals: string;
  };
  approvals: DeletionApproval[];
  /** Local conditions only; never operator eligibility. */
  readiness: {
    applicable: boolean;
    localChecksClear: boolean;
    reasons: string[];
    providerChecks: 'not_performed';
    operatorEligibility: 'determined_by_operator_dry_run';
  };
  auditEvents: { action: string; at: string }[];
  process: DeletionProcessStatus;
}

export interface DeletionListParams {
  page?: number;
  limit?: number;
  status?: '' | 'open' | DeletionLedgerStatus;
  userType?: '' | 'client' | 'stylist';
}

export async function listAccountDeletions(params: DeletionListParams = {}): Promise<DeletionRequestList> {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') searchParams.append(key, String(value));
  });
  const query = searchParams.toString();
  const { data } = await api.get(query ? `/v1/admin/account-deletions?${query}` : '/v1/admin/account-deletions');
  return data as DeletionRequestList;
}

export async function getAccountDeletion(id: string): Promise<DeletionRequestDetail> {
  const { data } = await api.get(`/v1/admin/account-deletions/${encodeURIComponent(id)}`);
  return (data as { success: boolean; data: DeletionRequestDetail }).data;
}
